import MandatoryBadge from '../../../../../features/discover/components/evaluation/MandatoryBadge'
import { isSet } from '../../../../../helpers'
import { StyleSheet, View } from '../../compiler'

import { colors, typoStyles } from '../../constants'
import { getColors } from '../../utils/matching-profile'
import BeamGraph from '../graphs/BeamGraph'
import BoltIcon from '../heroicons/micro/BoltIcon'
import ChatBubbleLeftRightIcon from '../heroicons/micro/ChatBubbleLeftRightIcon'
import ExclamationCircleIcon from '../heroicons/micro/ExclamationCircleIcon'
import MarkdownText from '../MarkdownText'
import RowListItem from '../RowListItem'

const styles = StyleSheet.create({
  dimensionTop: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    paddingLeft: 24 + 8,
    paddingRight: 24,
    height: 72,
    backgroundColor: '#fff',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor: '#EBF0F5'
  },
  dimensionBottom: {
    borderTopWidth: 1,
    backgroundColor: '#FAFBFC',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: '#EBF0F5'
  },
  fitMarker: {
    position: 'absolute',
    top: 0,
    left: 0,
    borderTopLeftRadius: 7,
    width: 16,
    height: '100%'
  },
  fitMarkerOverpaintRight: {
    position: 'absolute',
    top: 0,
    left: 7,
    backgroundColor: 'white',
    width: 12,
    height: '100%'
  },
  prioBadge: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: colors.paleBlue,
    borderRadius: 999,
    ...typoStyles.T2MediumBlue
  },
  header: { ...typoStyles.T2MediumBlue, textTransform: 'uppercase' },
  divider: { width: '100%', height: 1, backgroundColor: colors.lightGrey }
})

const getBeamGraphData = ({ match }) => {
  const {
    min_green,
    max_green,
    min_yellow,
    max_yellow,
    dimension_match,
    user_dimension_value
  } = match

  return {
    minYellow: min_yellow,
    maxYellow: max_yellow,
    minGreen: min_green,
    maxGreen: max_green,
    dimensionMatch: dimension_match,
    userDimensionValue: user_dimension_value
  }
}

const Divider = () => <View style={styles.divider}></View>

const InsightsSection = ({ text_modules }) => (
  <View style={{ padding: 24 }}>
    <MarkdownText style={typoStyles.T1Black}>
      {text_modules.insights}
    </MarkdownText>
  </View>
)

const MatchingDimensionCard = ({
  dimension,
  match,
  text_modules,
  insights_labels,
  language,
  displayTextModules,
  displayInsights = false,
  onlyDisplayChancesAndRisks = false
}) => {
  const { property, scaleEndpoints } = dimension
  const { left, right } = scaleEndpoints

  const display_text_modules = isSet(displayTextModules)
    ? displayTextModules
    : // low fit (<= 0.3, see getColors), continuous for RIASEC from scoring revision 2
      typeof match.dimension_match === 'number' && match.dimension_match <= 0.3
  const is_mandatory_criterion = !!match.mandatory_criterion

  const dimensionTopStyles = [
    styles.dimensionTop,
    !display_text_modules && { borderRadius: 8, borderWidth: 1 },
    displayInsights && { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
  ].filter((item) => !!item)

  const { medium } = getColors({ fit: match.dimension_match })

  const { risks_label, interviews_label, measures_label } = insights_labels

  if (onlyDisplayChancesAndRisks) {
    return (
      <View style={styles.dimensionBottom}>
        <View style={{ padding: 24 }}>
          <MarkdownText style={styles.header}>{risks_label}</MarkdownText>
          <View style={{ marginTop: 16, flexDirection: 'column', rowGap: 16 }}>
            {[text_modules.risks].map((paragraph, index) => (
              <RowListItem
                key={index}
                Icon={ExclamationCircleIcon}
                text={paragraph}
              />
            ))}
          </View>
        </View>
        <Divider />
        <View style={{ padding: 24 }}>
          <MarkdownText style={styles.header}>{interviews_label}</MarkdownText>
          <View style={{ marginTop: 16, flexDirection: 'column', rowGap: 16 }}>
            {[text_modules.interview].map((paragraph, index) => (
              <RowListItem
                key={index}
                Icon={ChatBubbleLeftRightIcon}
                text={paragraph}
              />
            ))}
          </View>
        </View>
        <Divider />
        <View style={{ padding: 24 }}>
          <MarkdownText style={styles.header}>{measures_label}</MarkdownText>
          <View style={{ marginTop: 16, flexDirection: 'column', rowGap: 16 }}>
            {[text_modules.measures].map((paragraph, index) => (
              <RowListItem key={index} Icon={BoltIcon} text={paragraph} />
            ))}
          </View>
        </View>
      </View>
    )
  }

  return (
    <>
      <View style={dimensionTopStyles}>
        <View
          style={[
            styles.fitMarker,
            {
              backgroundColor: medium,
              borderBottomLeftRadius:
                display_text_modules || displayInsights ? 0 : 7
            }
          ]}
        />
        <View style={styles.fitMarkerOverpaintRight} />
        <View
          className='whitespace-nowrap'
          style={{ flexDirection: 'row', alignItems: 'center' }}
        >
          <MarkdownText
            style={[typoStyles.H4Black, { textTransform: 'uppercase' }]}
          >
            {property[language]?.trim().length > 20
              ? `${property[language].trim().substring(0, 20)}...`
              : property[language]?.trim()}
          </MarkdownText>
          {is_mandatory_criterion && <MandatoryBadge className={'ml-2'} />}
        </View>
        <View style={{ display: 'flex', flexDirection: 'column' }}>
          <View style={{ display: 'flex', marginTop: 'auto' }} />
          <View style={{ width: 452 }}>
            <BeamGraph {...getBeamGraphData({ match })} />
          </View>
          <View
            style={{
              width: 452,
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 8
            }}
          >
            <MarkdownText style={typoStyles.SmallBlack}>
              {left.title[language]}
            </MarkdownText>
            <MarkdownText style={typoStyles.SmallBlack}>
              {right.title[language]}
            </MarkdownText>
          </View>
        </View>
      </View>
      {displayInsights && (
        <View style={styles.dimensionBottom}>
          <InsightsSection text_modules={text_modules} />
        </View>
      )}
      {display_text_modules && (
        <View style={styles.dimensionBottom}>
          {!displayInsights && (
            <>
              <InsightsSection text_modules={text_modules} />
              <Divider />
            </>
          )}
          <View style={{ padding: 24 }}>
            <MarkdownText style={styles.header}>{risks_label}</MarkdownText>
            <View
              style={{ marginTop: 16, flexDirection: 'column', rowGap: 16 }}
            >
              {[text_modules.risks].map((paragraph, index) => (
                <RowListItem
                  key={index}
                  Icon={ExclamationCircleIcon}
                  text={paragraph}
                />
              ))}
            </View>
          </View>
          <Divider />
          <View style={{ padding: 24 }}>
            <MarkdownText style={styles.header}>
              {interviews_label}
            </MarkdownText>
            <View
              style={{ marginTop: 16, flexDirection: 'column', rowGap: 16 }}
            >
              {[text_modules.interview].map((paragraph, index) => (
                <RowListItem
                  key={index}
                  Icon={ChatBubbleLeftRightIcon}
                  text={paragraph}
                />
              ))}
            </View>
          </View>
          <Divider />
          <View style={{ padding: 24 }}>
            <MarkdownText style={styles.header}>{measures_label}</MarkdownText>
            <View
              style={{ marginTop: 16, flexDirection: 'column', rowGap: 16 }}
            >
              {[text_modules.measures].map((paragraph, index) => (
                <RowListItem key={index} Icon={BoltIcon} text={paragraph} />
              ))}
            </View>
          </View>
        </View>
      )}
    </>
  )
}

export default MatchingDimensionCard
